import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@roberttamas/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { DietaryChips } from "@/components/dietary-chips";
import { Plus, Trash2 } from "@/components/icons";
import type { SetupStepProps } from "@/components/setup/steps";
import { StepSaveButton, YesNoStep } from "@/components/setup/yes-no-step";
import { updateMySiteConfig } from "@/lib/api";
import { makeId } from "@/lib/draft-ids";
import { toAppError } from "@/lib/errors";
import {
  DEFAULT_MENU_ICON,
  MENU_ICON_OPTIONS,
  MENU_MAX_GROUPS,
  MENU_MAX_ITEMS,
} from "@/lib/menu";
import type {
  DietaryTag,
  MenuConfig,
  MenuIconKey,
} from "@/lib/types";

interface DraftItem {
  id: string;
  name: string;
  description: string;
  dietary: DietaryTag[];
}

interface DraftCourse {
  id: string;
  name: string;
  time: string;
  icon: MenuIconKey;
  items: DraftItem[];
}

function emptyItem(): DraftItem {
  return { id: makeId(), name: "", description: "", dietary: [] };
}

function emptyCourse(name = ""): DraftCourse {
  return { id: makeId(), name, time: "", icon: DEFAULT_MENU_ICON, items: [emptyItem()] };
}

function toDraft(menu: MenuConfig | undefined): DraftCourse[] {
  const courses = (menu?.groups ?? []).map((g) => ({
    id: g.id,
    name: g.name,
    time: g.time ?? "",
    icon: g.icon,
    items: g.items.map((it) => ({
      id: it.id,
      name: it.name,
      description: it.description ?? "",
      dietary: it.dietary ?? [],
    })),
  }));
  return courses.length > 0 ? courses : [emptyCourse()];
}

/**
 * Wizard menu step — "Do you want to show a menu?" then the simplest
 * possible guided editor: courses with dishes, optional details. Saves
 * the content and flips the guest-facing toggle in one call.
 */
export function StepMenu({
  orderId,
  setup,
  linear,
  onDone,
  onBack,
}: SetupStepProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { site, config } = setup;
  const siteId = site?.siteId ?? "";

  const [enabled, setEnabled] = useState<boolean | null>(
    config?.permissions?.allowMenu === undefined && !config?.menu
      ? null
      : (config?.permissions?.allowMenu ?? false),
  );
  const [courses, setCourses] = useState<DraftCourse[]>(() =>
    toDraft(config?.menu),
  );

  const totalItems = courses.reduce((n, c) => n + c.items.length, 0);

  const updateCourse = (id: string, patch: Partial<DraftCourse>) =>
    setCourses((prev) =>
      prev.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    );
  const updateItem = (
    courseId: string,
    itemId: string,
    patch: Partial<DraftItem>,
  ) =>
    setCourses((prev) =>
      prev.map((c) =>
        c.id === courseId
          ? {
              ...c,
              items: c.items.map((it) =>
                it.id === itemId ? { ...it, ...patch } : it,
              ),
            }
          : c,
      ),
    );

  const mutation = useMutation({
    mutationFn: () => {
      if (enabled === false) {
        return updateMySiteConfig({
          siteId,
          configPatch: { permissions: { allowMenu: false } },
        });
      }
      const groups = courses
        .filter((c) => c.name.trim())
        .map((c) => ({
          id: c.id,
          name: c.name.trim(),
          ...(c.time.trim() ? { time: c.time.trim() } : {}),
          icon: c.icon,
          items: c.items
            .filter((it) => it.name.trim())
            .map((it) => ({
              id: it.id,
              name: it.name.trim(),
              ...(it.description.trim()
                ? { description: it.description.trim() }
                : {}),
              ...(it.dietary.length ? { dietary: it.dietary } : {}),
            })),
        }));
      const menu: MenuConfig = { groups };
      return updateMySiteConfig({
        siteId,
        configPatch: { menu, permissions: { allowMenu: true } },
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["mySetup", orderId] });
      onDone();
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  const save = () => {
    if (enabled === null) {
      toast.error(t("setup.pickYesNo"));
      return;
    }
    if (enabled && !courses.some((c) => c.name.trim())) {
      toast.error(t("setup.menu.nameCourse"));
      return;
    }
    mutation.mutate();
  };

  return (
    <YesNoStep
      stepId="menu"
      yesLabel={t("setup.menu.yes")}
      noLabel={t("setup.menu.no")}
      enabled={enabled}
      onChoose={setEnabled}
      onBack={onBack}
      footer={
        <StepSaveButton
          saving={mutation.isPending}
          disabled={enabled === null}
          linear={linear}
          onClick={save}
        />
      }
    >
      <div className="space-y-5">
        {courses.map((course, ci) => (
          <div key={course.id} className="space-y-3 rounded-lg border p-4">
            <div className="flex items-end gap-2">
              <div className="min-w-0 flex-1 space-y-1">
                <p className="text-xs uppercase text-muted-foreground">
                  {t("setup.menu.courseN", { n: ci + 1 })}
                </p>
                <Input
                  placeholder={t("setup.menu.coursePlaceholder")}
                  value={course.name}
                  onChange={(e) =>
                    updateCourse(course.id, { name: e.target.value })
                  }
                />
              </div>
              <Input
                type="time"
                className="w-28"
                value={course.time}
                onChange={(e) =>
                  updateCourse(course.id, { time: e.target.value })
                }
              />
              <Select
                value={course.icon}
                onValueChange={(icon) =>
                  updateCourse(course.id, { icon: icon as MenuIconKey })
                }
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MENU_ICON_OPTIONS.map((opt) => (
                    <SelectItem key={opt.key} value={opt.key}>
                      {t(opt.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {courses.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={t("setup.menu.removeCourse")}
                  onClick={() =>
                    setCourses((prev) => prev.filter((c) => c.id !== course.id))
                  }
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>

            <div className="space-y-3">
              {course.items.map((item) => (
                <div key={item.id} className="space-y-2 border-l-2 pl-3">
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder={t("setup.menu.dishPlaceholder")}
                      value={item.name}
                      onChange={(e) =>
                        updateItem(course.id, item.id, { name: e.target.value })
                      }
                    />
                    {course.items.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={t("setup.menu.removeDish")}
                        onClick={() =>
                          updateCourse(course.id, {
                            items: course.items.filter(
                              (it) => it.id !== item.id,
                            ),
                          })
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <DietaryChips
                    value={item.dietary}
                    onChange={(dietary) =>
                      updateItem(course.id, item.id, { dietary })
                    }
                  />
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={totalItems >= MENU_MAX_ITEMS}
                onClick={() =>
                  updateCourse(course.id, {
                    items: [...course.items, emptyItem()],
                  })
                }
              >
                <Plus className="h-4 w-4" /> {t("setup.menu.addDish")}
              </Button>
            </div>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          disabled={courses.length >= MENU_MAX_GROUPS}
          onClick={() => setCourses((prev) => [...prev, emptyCourse()])}
        >
          <Plus className="h-4 w-4" /> {t("setup.menu.addCourse")}
        </Button>
      </div>
    </YesNoStep>
  );
}
